import React, { useEffect, useMemo, useState } from 'react';
import { syncMasterData } from '../../../services/masterDataSyncService';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import PackScreen from './pack/PackScreen';
import SizeScreen from './size/SizeScreen';
import ColourScreen from './colour/ColourScreen';
import GradeScreen from './grade/GradeScreen';
import VarietyScreen from './variety/VarietyScreen';
import CommodityStateScreen from './commodityState/CommodityStateScreen';
import { camelCase, compareString } from '../../../services/appFunctionsService';
import { ICommodity } from '../../../@types/model/masterData/commodity/commodity';
import CommodityHttpService from '../../../services/http/masterData/commodityHttpService';
import { generalShowErrorSnackbar } from '../../../store/general/Functions';
import TextInput from '../../../components/input/TextInput';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import OrchardScreen from './orchard/OrchardScreen';
import { useRouteMatch } from 'react-router';
import Screen from '../../../components/Screen';
import SeasonScreen from './season/SeasonScreen';
import { getCommodityImage } from '../../../services/iconHelperService';
import materialTheme, { getThemeMode } from '../../../styles/materialTheme';
import { dataSetCommodities, dataSetSelectedCommodity } from '../../../store/masterData/Actions';
import { navPath } from '../../../store/nav/Actions';
import { CustomTheme } from '@mui/material';
import { IApiError } from '../../../@types/other';

const themeMode = getThemeMode();

interface IRouteParams {
    section ?: string;
}

interface ICommodityListTheme extends CustomTheme {
    commodityListItem : {
        selected : {
            background : string;
            text : string;
        };
        default : {
            text : string;
        };
    };
}

const CommoditySectionScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const commodities = useAppSelector(x => x.masterData.commodities);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);
    const match = useRouteMatch<IRouteParams>();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedCommodity, setSelectedCommodity] = useState<ICommodity | null>(null);
    const [commoditySearchValue, setCommoditySearchValue] = useState<string>('');
    const [selectedTab, setSelectedTab] = useState<number>(0);

    const tabNames = [
        'Variety',
        'Grade',
        'Colour',
        'Pack',
        'Size',
        'Orchard',
        'Commodity State',
        'Season',
    ];

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await CommodityHttpService.getCommodityData();
                    dispatch(dataSetCommodities(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError | undefined;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading commodities.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();

        if (!match.params.section || match.params.section === 'Pack') {
            navPath('/masterData/management/commoditySection.pack');
        }
    }, [match.params.section]);

    useEffect(() => {
        if (!match.params.section) {
            setSelectedTab(0);
            return;
        }
        const tabIndex = tabNames.findIndex(x => match.params.section?.split('.')[1].includes(camelCase(x)));

        setSelectedTab(tabIndex >= 0 ? tabIndex : 0);
    }, [match.params.section]);

    useEffect(() => {
        if ((selectedTab && tabNames.length < selectedTab)) {
            setSelectedTab(0);
        } else if (tabNames.length < 1) {
            setSelectedTab(0);
        }
    }, [selectedTab]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    // const getTabContents = (index : number) => {

    // };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    // const tabContent = useMemo(() => {
    //     switch (tabs) {
    //         case tabs[0]:
    //             return <VarietyScreen selectedCommodity={selectedCommodity ?? undefined}/>;
    //     }
    // }, [selectedCommodity]);

    const tabs = useMemo(() => ({
        Variety: <VarietyScreen selectedCommodity={selectedCommodity}/>,
        Grade: <GradeScreen selectedCommodity={selectedCommodity}/>,
        Colour: <ColourScreen selectedCommodity={selectedCommodity}/>,
        Pack: <PackScreen selectedCommodity={selectedCommodity} />,
        Size: <SizeScreen selectedCommodity={selectedCommodity}/>,
        Orchard: <OrchardScreen selectedCommodity={selectedCommodity}/>,
        'Commodity State': <CommodityStateScreen selectedCommodity={selectedCommodity}/>,
        Season: <SeasonScreen selectedCommodity={selectedCommodity}/>,
    }), [selectedCommodity]);

    const onCommoditySelect = useMemo(() => (commodityId : number) => {
        const newCommodity = (selectedCommodity?.id !== commodityId
            ? commodities.find(x => x.id === commodityId)
            : null) ?? null;
        setSelectedCommodity(newCommodity);
        dispatch(dataSetSelectedCommodity(newCommodity ?? undefined));
    }, [selectedCommodity, commodities]);

    const rows = useMemo(() => {
        return commodities.filter(x => x.isActive && x.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y)));
    }, [commodities, selectedOrganizationIds]);

    const getTabContents = useMemo(() => (index : number) => Object.values(tabs)[index], [tabs]);

    const handleChange = useMemo(() => (_event : React.SyntheticEvent, newSelectedTab : number) => {
        navPath(`/masterData/management/commoditySection.${camelCase(Object.keys(tabs)[newSelectedTab])}`);
    }, [tabs]);

    const theme = useMemo(() => materialTheme, []);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isPadded={false} isScrollable={false} isLoading={isLoading}>
            <div className={'fdc hfill'}>
                <div className={'fdr hfill'}>
                    <div className={'fdc flx1'}>
                        <div className={'posstick post0 zi1 bb1 cgray2'}>
                            <div className={'fdr p10'}>
                                <TextInput
                                    name={'search-commodities'}
                                    label={'Search Commodities'}
                                    value={commoditySearchValue}
                                    onChange={e => setCommoditySearchValue(e.target.value)}
                                />
                            </div>
                        </div>
                        <List component={'nav'} className={'hfill p5 oys'}>
                            {rows.sort((x, y) => compareString(x.name ?? '', y.name ?? '')).map((element) => {
                                const customTheme = theme.custom as unknown as ICommodityListTheme;
                                const background = selectedCommodity?.id === element.id
                                    ? customTheme.commodityListItem.selected.background
                                    : '';
                                const textColor = selectedCommodity?.id === element.id
                                    ? customTheme.commodityListItem.selected.text
                                    : customTheme.commodityListItem.default.text;
                                return !!element.name && element.name.toUpperCase().includes(commoditySearchValue.toUpperCase()) &&
                                    <ListItem
                                        key={'commodity_' + element.id}
                                        style={{ backgroundColor: background, color: textColor }}
                                        button
                                        selected={selectedCommodity?.id === element.id}
                                        onClick={() => onCommoditySelect(element.id)}
                                    >
                                        <img
                                            src={getCommodityImage(element.name)}
                                            height={30}
                                            width={30}
                                            className={'pr10' + (selectedCommodity?.id === element.id ? ' commodityListIconSelected' : '')}
                                        />
                                        {element.name}
                                    </ListItem>;
                            })}
                        </List>
                    </div>
                    <div className={'fdc flx5 oxh'}>
                        <TabContext value={selectedTab.toString()}>
                            <TabList
                                onChange={handleChange}
                                indicatorColor={themeMode === 'light' ? 'primary' : 'secondary'}
                                textColor={themeMode === 'light' ? 'primary' : 'secondary'}
                            >
                                {tabNames.map((tabName, index) => (
                                    <Tab
                                        key={'tab' + tabName}
                                        label={`${tabName === 'Pack' ? 'Pack Configuration' : tabName}`}
                                        value={index.toString()}
                                    />
                                ))}
                            </TabList>
                            {tabNames.map((tabName, index) => (
                                <TabPanel
                                    style={{ height: 'calc(100% - 48px)' }}
                                    key={'tabPanel' + tabName}
                                    value={index.toString()}
                                >
                                    {getTabContents(index)}
                                </TabPanel>
                            ))}
                        </TabContext>
                    </div>
                </div>
            </div>
        </Screen>
    );
};

export default CommoditySectionScreen;
